import { api } from '../../lib/api-client';

export type FinancingStatus = 'PENDING' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN';
export type ChecklistItemStatus = 'PENDING' | 'RECEIVED' | 'WAIVED' | 'NOT_APPLICABLE';

export interface ChecklistItem {
  uid: string;
  code: string;
  label: string;
  isMandatory: boolean;
  status: ChecklistItemStatus;
  note: string | null;
  attachmentUid: string | null;
  dueAt: string | null;
  completedAt: string | null;
}

export interface Checklist {
  uid: string;
  templateCode: string;
  label: string;
  items: ChecklistItem[];
  /** Mandatory items still outstanding. Empty means the bank can have it. */
  outstanding: string[];
  isComplete: boolean;
}

/** Money is a decimal STRING on this boundary — see money.ts for why. */
export interface FinancingApplication {
  uid: string;
  applicationNo: string;
  status: FinancingStatus;
  bookingUid: string;
  bookingNo: string;
  customerUid: string;
  customerName: string;
  providerUid: string;
  providerName: string;
  providerRefNo: string | null;
  amount: string;
  tenureMonths: number;
  currency: string;
  indicativeMonthly: string;
  submittedAt: string | null;
  decidedAt: string | null;
  declineReason: string | null;
  withdrawReason: string | null;
  officerName: string | null;
  officerPhone: string | null;
  daysAwaitingDecision: number | null;
  checklist: Checklist | null;
  branchCode: string | null;
  createdAt: string;
}

export interface FinancingPage {
  items: FinancingApplication[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface FinancingProvider {
  uid: string;
  code: string;
  name: string;
  contactName: string | null;
  contactPhone: string | null;
  isActive: boolean;
}

export interface ChecklistTemplate {
  code: string;
  label: string;
  items: { code: string; label: string; isMandatory: boolean }[];
}

export function listFinancing(params: {
  q?: string;
  status?: FinancingStatus;
  bookingUid?: string;
  awaitingLongerThanDays?: number;
  cursor?: string;
}): Promise<FinancingPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.bookingUid !== undefined) search.set('bookingUid', params.bookingUid);
  if (params.awaitingLongerThanDays !== undefined) {
    search.set('awaitingLongerThanDays', String(params.awaitingLongerThanDays));
  }
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<FinancingPage>(`/financing${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getFinancing(uid: string): Promise<FinancingApplication> {
  return api<FinancingApplication>(`/financing/${uid}`);
}

export function listProviders(): Promise<FinancingProvider[]> {
  return api<FinancingProvider[]>('/financing/providers');
}

export function listChecklistTemplates(): Promise<ChecklistTemplate[]> {
  return api<ChecklistTemplate[]>('/financing/checklist-templates');
}

export function createFinancing(input: {
  bookingUid: string;
  providerUid: string;
  templateCode: string;
  tenureMonths: number;
  amount?: string;
  officerName?: string;
  officerPhone?: string;
}): Promise<FinancingApplication> {
  return api<FinancingApplication>('/financing', { method: 'POST', body: input });
}

export function submitFinancing(
  uid: string,
  input: { providerRefNo?: string },
): Promise<FinancingApplication> {
  return api<FinancingApplication>(`/financing/${uid}/transitions/submit`, {
    method: 'POST',
    body: input,
  });
}

export function decideFinancing(
  uid: string,
  input: { status: 'APPROVED' | 'REJECTED'; reason?: string; providerRefNo?: string },
): Promise<FinancingApplication> {
  return api<FinancingApplication>(`/financing/${uid}/transitions/decide`, {
    method: 'POST',
    body: input,
  });
}

export function withdrawFinancing(uid: string, input: { reason: string }): Promise<FinancingApplication> {
  return api<FinancingApplication>(`/financing/${uid}/transitions/withdraw`, {
    method: 'POST',
    body: input,
  });
}

export function updateChecklistItem(
  itemUid: string,
  input: { status: ChecklistItemStatus; note?: string; attachmentUid?: string },
): Promise<ChecklistItem> {
  return api<ChecklistItem>(`/financing/checklist-items/${itemUid}`, {
    method: 'PATCH',
    body: input,
  });
}
